/**
 * LiveInterviewRoomPage — candidate-facing Live Interview room.
 * Unauthenticated: the URL's `token` is the credential (see
 * services/liveInterviewPublicApi.ts), same pattern as PreJoinPage. Reached
 * via PreJoinPage's `navigate` on Join.
 *
 * Uses the SAME MeetingStage as the recruiter room — identical video grid,
 * pagination, screen sharing, reactions and control bar — with a lighter
 * side panel: just the revealed question's answer surface plus chat. That
 * panel is collapsible but (unlike the recruiter's) not resizable; there is
 * only ever one thing in it at a time, so a drag handle would be noise.
 *
 * When the recruiter's room sends a show-question signal over the shared
 * signaling WebSocket, this page reveals the question and lets the candidate
 * answer it (reusing Proctored Interview's CodeEditor/WhiteboardCanvas for
 * code/whiteboard questions, a plain textarea otherwise), submitting via the
 * public, token-gated answer endpoint — and then announcing
 * `answer-submitted` back over the same socket, because that REST call is
 * invisible to the recruiter's room otherwise (see handleSubmitAnswer).
 *
 * VERBAL (SPOKEN) QUESTIONS — the fourth answer surface. A verbal question
 * used to have no candidate-side answer path at all: the recruiter's own "OK"
 * button submitted modality="audio" with transcript=null, which is scored
 * against a placeholder and therefore LOWERED the candidate's ability estimate
 * every time it was used. It was never the neutral "mark as asked" it looked
 * like. Now the candidate answers it themselves, by SPEAKING: the answer is
 * recorded here and transcribed server-side with Azure Whisper.
 *
 * WHAT GETS RECORDED is not just this candidate's microphone. Their own mic
 * AND every interviewer's incoming audio are mixed into one stream through a
 * Web Audio graph before recording, because a transcript of one half of a
 * conversation is close to useless for review or scoring — the question, the
 * follow-ups and the interruptions all belong in it.
 *
 * (This replaces an earlier client-side Web Speech API approach. That
 * transcribed in the browser with no audio ever leaving it, which was simpler,
 * but it only ever heard this one microphone, its accuracy varied wildly by
 * browser, and it left nothing anyone could listen back to.)
 *
 * RETRY WITHOUT RE-RECORDING: the finalized clip is kept after a failed
 * submission, and the submit button becomes "Retry Submission". A transient
 * network or transcription failure must never make someone deliver their whole
 * spoken answer a second time. Only a successful submit, or a genuinely new
 * question, discards the recording.
 *
 * Two escape hatches keep anyone from being stranded: the recruiter can hand
 * this candidate a typed box for the current question instead
 * (`verbal-text-box-state`), and a browser with no MediaRecorder falls back to
 * the same typed box automatically. See `answerModality`.
 *
 * HANG UP: this page offers "Leave" and only "Leave". Ending the interview
 * for everyone is the recruiter's action alone, so no onEndInterview is
 * passed to MeetingStage here. The reverse direction is handled: the
 * recruiter's End Interview arrives as `interview-ended` and tears this room
 * down immediately.
 *
 * Recording: the composited recording is produced HERE, in the candidate's
 * browser. It draws from MeetingStage's off-screen <video> pool rather than
 * the visible tiles — see MeetingStage's header for why that separation is
 * load-bearing, and for the documented trade-off that a candidate who shares
 * their screen still has their camera (not the screen) recorded.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { ShieldCheckIcon, PaperAirplaneIcon, MicrophoneIcon, StarIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  getSession, getPublicTurnCredentials, submitPublicAnswer, getPublicChatMessages, markPublicChatSeen, getPublicTranscript,
  runPublicCode, runPublicTestCases, postRecordingChunk, getPublicSpeechToken, submitFeedback, recordIntegrityEvent,
  startAiInterviewer, sendAiInterviewerTurn, forceStartAiInterviewer, pollAiInterviewerEvents, endAiInterviewer, type AiInterviewerResponse,
  type SubmitPublicAnswerPayload,
} from '@/services/liveInterviewPublicApi'
import { useLiveInterviewRecorder, type RecorderTile } from '@/hooks/useLiveInterviewRecorder'
import { useLiveInterviewMeeting, peerLeftNotice } from '@/hooks/useLiveInterviewMeeting'
import type { LiveInterviewIceServer } from '@/hooks/useLiveInterviewCall'
import useDictation from '@/hooks/useDictation'
import { isAddressedToAi } from '@/utils/aiInterviewerIntent'
import useLiveTranscription from '@/hooks/useLiveTranscription'
import { useAiInterviewerVoice } from '@/hooks/useAiInterviewerVoice'
import useIntegrityMonitor from '@/hooks/useIntegrityMonitor'
import { CodeEditor } from '@/pages/publicInterview/PublicInterviewPage'
import WhiteboardCanvas, { type WhiteboardCanvasHandle } from '@/components/proctoredInterview/WhiteboardCanvas'
import LiveInterviewChatPanel, { type LiveInterviewChatMessage } from '@/components/liveInterview/LiveInterviewChatPanel'
import LiveInterviewTranscriptPanel, { type LiveTranscriptSegment, MAX_LIVE_TRANSCRIPT_SEGMENTS } from '@/components/liveInterview/LiveInterviewTranscriptPanel'
import MeetingStage, { AiThinkingDots, AiVoiceBars, type AiPresenceState } from '@/components/liveInterview/MeetingStage'
import MeetingSidePanel from '@/components/liveInterview/MeetingSidePanel'

interface RevealedQuestion {
  id: string
  question_text: string
  skill_name?: string
  question_type_id?: string
  response_mode?: string | null
  category?: string | null
  test_cases?: { input: string; expected_output: string; description?: string }[]
  /** AI-interviewer mode only — drives the per-question countdown timer. */
  duration_minutes?: number | null
}

/**
 * Whether this browser can record audio at all. Feature-detected ONCE at
 * module load. Everything downstream treats `false` as a normal, supported
 * situation — the candidate simply gets the same typed box the recruiter's
 * override hands out — never as an error, because leaving someone with no way
 * to answer at all is far worse than making them type.
 */
// AI-interviewer mode: how long the candidate must stay silent before their
// buffered speech is sent to the AI as one complete message.
const AI_TURN_SILENCE_MS = 2500

// Human-mode browser-behaviour integrity events (tab switch, copy/cut/paste,
// extra display): each type is reported at most once per this window, so a
// burst (e.g. repeated paste attempts) doesn't flood the log or the
// recruiter's alerts. Same 5s the camera/mic monitor uses per type.
const INTEGRITY_BEHAVIOUR_THROTTLE_MS = 5000

const VERBAL_CAPTURE_SUPPORTED = typeof MediaRecorder !== 'undefined'

/**
 * Container formats to record the spoken answer in, best first. Opus in WebM
 * is what Whisper handles most reliably and what every Chromium browser
 * produces; the Ogg entry covers Firefox, and an empty result means "let the
 * browser pick" rather than refusing to record.
 */
const VERBAL_MIME_PREFERENCES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']

/** `audio/webm;codecs=opus` -> `webm`. Sent alongside the clip so the server
 *  hands Whisper a temp file with the extension it dispatches on. */
function containerExtension(mimeType: string): string {
  const match = /^audio\/([^;]+)/.exec(mimeType || '')
  return match ? match[1].toLowerCase() : 'webm'
}

/**
 * One in-flight recording of a spoken answer: the Web Audio graph mixing every
 * participant's audio, the recorder consuming it, and — crucially — that
 * recorder's OWN chunk list and mixed-stream set rather than shared ones. See
 * `captureRef` for why that ownership is load-bearing.
 */
interface VerbalCapture {
  context: AudioContext
  destination: MediaStreamAudioDestinationNode
  recorder: MediaRecorder
  chunks: Blob[]
  /** Streams already wired in, keyed by peer AND stream id — a renegotiation
   *  can hand the same peer a brand-new MediaStream that does need mixing. */
  mixed: Set<string>
}

/**
 * Bare base64 for one recorded clip — no `data:` URL prefix, which is what the
 * backend's b64decode expects. FileReader is the only way to get base64 out of
 * a Blob without reading it into a string by hand, and it only offers a data
 * URL, so the prefix is sliced off at the first comma.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

export default function LiveInterviewRoomPage() {
  const { token } = useParams<{ token: string }>()
  const recorder = useLiveInterviewRecorder()

  const [loadingSession, setLoadingSession] = useState(true)
  const [sessionError, setSessionError] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [chunkIntervalMs, setChunkIntervalMs] = useState(10000)
  // Server-configured signaling-reconnect budget. Same plumbing as
  // chunkIntervalMs: read once from the session GET this page already makes,
  // never hardcoded in the bundle.
  const [reconnectTimeoutMs, setReconnectTimeoutMs] = useState(60000)
  // Same pattern — server-configured live-transcript scrollback cap.
  const [maxLiveTranscriptSegments, setMaxLiveTranscriptSegments] = useState(MAX_LIVE_TRANSCRIPT_SEGMENTS)
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [mediaError, setMediaError] = useState(false)
  const [iceServers, setIceServers] = useState<LiveInterviewIceServer[] | null>(null)

  // Both modes (human mode without the multi-voice check - see the
  // useIntegrityMonitor call below) — same client-side integrity monitoring
  // (face/object detection, camera-blocked, multi-audio-source heuristics)
  // Proctored Interview already runs, reported through the SAME backend
  // endpoint Live Interview's own pre-join screen already calls
  // (recordIntegrityEvent) - that endpoint and its DB model already existed
  // and were already being hit for the pre-join checks, but nothing ever
  // actually monitored DURING the interview itself. A visually hidden
  // <video> bound to this candidate's own camera feed is all the hook needs
  // to read frames from - no camera box was added, mirroring how Proctored
  // Interview feeds it during its own camera-live-but-no-preview stages.
  const integrityVideoRef = useRef<HTMLVideoElement>(null)
  const [integrityVideoEl, setIntegrityVideoEl] = useState<HTMLVideoElement | null>(null)
  useEffect(() => {
    const el = integrityVideoRef.current
    if (el) el.srcObject = mediaStream
    setIntegrityVideoEl(el)
  }, [mediaStream])

  const [question, setQuestion] = useState<RevealedQuestion | null>(null)
  const [answerText, setAnswerText] = useState('')
  const dictation = useDictation(answerText, setAnswerText)
  const [codeLanguage, setCodeLanguage] = useState('python3')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const whiteboardRef = useRef<WhiteboardCanvasHandle>(null)
  // --- Verbal (spoken) answer capture ----------------------------------
  // The candidate's spoken answer is RECORDED here and transcribed
  // server-side (Azure Whisper, via the submit endpoint) — there is no
  // client-side speech recognition and therefore no live transcript to show.
  // What the candidate sees instead is simply that recording is happening.
  const [verbalRecording, setVerbalRecording] = useState(false)
  // True once a submission carrying a recorded clip has failed. The clip
  // SURVIVES that failure (see verbalBlobRef) — this only drives the "Retry
  // Submission" wording, so the candidate knows the next click re-sends what
  // they already said rather than starting over.
  const [verbalSubmitFailed, setVerbalSubmitFailed] = useState(false)
  // The recruiter's per-question override (their "Show Text Box" button),
  // arriving as a `verbal-text-box-state` signal or on the reveal itself.
  const [verbalTextBoxEnabled, setVerbalTextBoxEnabled] = useState(false)

  // The finalized recording, held in a ref rather than state because it is
  // read inside async submit flows where a stale closure would silently
  // re-send nothing. It is cleared ONLY by a successful submit or a genuinely
  // new question — never by a failure, which is the entire retry story: a
  // transient network/transcription error must not cost the candidate their
  // spoken answer.
  const verbalBlobRef = useRef<Blob | null>(null)
  // Every Run / Run Test Cases attempt made on the CURRENT coding question,
  // oldest first — the /run and /run-tests endpoints are themselves
  // stateless (see live_interview_public.py's run_public_code docstring), so
  // this ref is what actually accumulates the history; it rides along on the
  // next handleSubmitAnswer call and is reset whenever a new question is
  // revealed (same lifecycle as verbalBlobRef above).
  const codeRunHistoryRef = useRef<NonNullable<SubmitPublicAnswerPayload['run_history']>>([])
  // The ONE in-flight capture, or null. Bundling the graph, the recorder, its
  // chunks and its dedupe set into a single object is what makes overlapping
  // captures safe, and they DO overlap: stopping a MediaRecorder is
  // asynchronous (the final `ondataavailable` and `onstop` fire later), so
  // when one question is replaced by the next, the outgoing recorder is still
  // flushing while the incoming one is already recording. With these held as
  // separate refs, the outgoing recorder's last chunk lands in the incoming
  // question's chunk list and the outgoing teardown closes the incoming
  // AudioContext — the new answer records into a corpse. Owning its own state
  // per capture makes that structurally impossible rather than a matter of
  // getting the ordering right.
  const captureRef = useRef<VerbalCapture | null>(null)
  const [chatMessages, setChatMessages] = useState<LiveInterviewChatMessage[]>([])
  const [transcriptSegments, setTranscriptSegments] = useState<LiveTranscriptSegment[]>([])
  const [panelOpen, setPanelOpen] = useState(true)
  const panelOpenRef = useRef(panelOpen)
  useEffect(() => { panelOpenRef.current = panelOpen }, [panelOpen])
  // Interview vs. Chat now live in separate side-panel tabs instead of one
  // long scrolling column — refs shadow the state the WS message handler
  // (set up once, well before either can change) needs at message-arrival time.
  const [activeTab, setActiveTab] = useState<'interview' | 'chat' | 'transcript'>('interview')
  const activeTabRef = useRef(activeTab)
  useEffect(() => { activeTabRef.current = activeTab }, [activeTab])
  // Guided tour — same mechanism as Proctored Interview's own
  // (PublicInterviewPage's InterviewTour): null = inactive, 0 = intro card,
  // 1..LIVE_INTERVIEW_TOUR_STEPS.length = a spotlighted step. Shown once,
  // right when the candidate's connection actually comes up — not on mount,
  // which would pop it over a still-connecting/blank stage.
  const [tourStep, setTourStep] = useState<number | null>(null)
  const tourStartedRef = useRef(false)
  const [unreadChat, setUnreadChat] = useState(0)
  const [otherTyping, setOtherTyping] = useState(false)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set when the recruiter's "End Interview" is relayed here as
  // `interview-ended` — the candidate never has an action that sets this.
  const [interviewEnded, setInterviewEnded] = useState(false)
  // Driven by the recruiter's explicit Start/Stop Recording control (a
  // 'recording-state' broadcast — see RecruiterRoomPage/MeetingStage) rather
  // than assumed always-on: recording is no longer automatic, so this side
  // needs the real state to know whether to say so.
  const [isRecording, setIsRecording] = useState(false)

  // --- AI interviewer mode (Phase F) -----------------------------------
  // "human" (a recruiter conducts the call, as above) or "ai" (no recruiter
  // ever joins — see startAiInterviewer/handleAiTurnResult below). Read once
  // from getSession, alongside chunkIntervalMs etc.
  const [interviewMode, setInterviewMode] = useState<'human' | 'ai'>('human')
  const aiMode = interviewMode === 'ai'

  // Every integrity event during the call goes through reportIntegrityEvent
  // (defined further down, once the meeting connection exists): it's stored
  // via recordIntegrityEvent AND sent live to the recruiter's room. A ref, so
  // the monitor below can call it before that point in the render.
  const reportIntegrityRef = useRef<(type: string, details?: Record<string, any>) => void>(() => {})

  // See integrityVideoRef's own doc comment above for why this is wired here
  // at all. Runs in BOTH modes, for the same span the call recording covers.
  // Human mode leaves out the multi-voice heuristic (micStream: null turns
  // just that check off): the recruiter's own voice coming out of the
  // candidate's speakers would set it off.
  useIntegrityMonitor({
    videoEl: integrityVideoEl,
    micStream: aiMode ? mediaStream : null,
    active: !loadingSession && !sessionError && !interviewEnded && !!mediaStream,
    onEvent: (type, details) => reportIntegrityRef.current(type, details),
  })

  // Azure Neural TTS via the same candidate speech token as transcription
  // (falls back to the browser voice if Azure is unavailable).
  // How much of the line being spoken has actually been HEARD (from the voice
  // hook) - drives the caption's "sentence being spoken" highlight. Matched
  // against aiDisplayedText at render time, so progress for another line
  // (e.g. the closing line after endAiInterviewer) is ignored.
  const [aiSpeechProgress, setAiSpeechProgress] = useState<{ text: string; spokenChars: number } | null>(null)
  const aiVoice = useAiInterviewerVoice(
    token ? () => getPublicSpeechToken(token) : undefined,
    (stream) => recorder.addAudioStream(stream),
    (text, spokenChars) => setAiSpeechProgress({ text, spokenChars }),
  )
  // The line currently being spoken — awaited before the interview ends, so
  // the AI's closing words are never cut off by the switch to the thank-you
  // screen (or by a second line replacing it).
  const lastAiSpeechRef = useRef<Promise<void>>(Promise.resolve())
  const waitForAiSpeech = (capMs = 45000) =>
    Promise.race([lastAiSpeechRef.current.catch(() => {}), new Promise<void>((r) => setTimeout(r, capMs))])

  // AI-interviewer mode records the candidate's ENTIRE screen (with their
  // camera inset). 'pending' = not shared yet (the AI waits), 'granted' =
  // sharing, 'unsupported' = this browser can't capture the screen, so the
  // interview runs with the normal camera recording instead.
  const [screenShareStatus, setScreenShareStatus] = useState<'pending' | 'granted' | 'unsupported'>(
    () => (typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function' ? 'pending' : 'unsupported'),
  )
  const [screenSharingStopped, setScreenSharingStopped] = useState(false)
  const screenStreamRef = useRef<MediaStream | null>(null)
  const screenVideoRef = useRef<HTMLVideoElement | null>(null)
  const [requestingScreen, setRequestingScreen] = useState(false)
  const requestScreenShare = async () => {
    if (requestingScreen) return
    setRequestingScreen(true)
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor', width: { ideal: 1920 }, height: { ideal: 1080 } },
        // The AI's voice and the candidate's mic are mixed in directly;
        // system audio would only double them up.
        audio: false,
        selfBrowserSurface: 'include',
        preferCurrentTab: false,
      } as DisplayMediaStreamOptions)
      const track = stream.getVideoTracks()[0]
      const surface = (track?.getSettings() as MediaTrackSettings & { displaySurface?: string })?.displaySurface
      if (surface && surface !== 'monitor') {
        stream.getTracks().forEach((t) => t.stop())
        toast.error('Please choose "Entire screen" in the share dialog — the whole screen is recorded during this interview.')
        return
      }
      screenStreamRef.current?.getTracks().forEach((t) => t.stop())
      screenStreamRef.current = stream
      const video = document.createElement('video')
      video.muted = true
      video.playsInline = true
      video.srcObject = stream
      video.play().catch(() => {})
      screenVideoRef.current = video
      recorder.setScreenSource(video)
      track?.addEventListener('ended', () => {
        if (screenStreamRef.current !== stream) return
        screenStreamRef.current = null
        screenVideoRef.current = null
        recorder.setScreenSource(null)
        setScreenSharingStopped(true)
      })
      setScreenSharingStopped(false)
      setScreenShareStatus('granted')
    } catch {
      toast.error('Screen sharing is required to start this AI interview. Please allow it to continue.')
    } finally {
      setRequestingScreen(false)
    }
  }

  // AI presence: what the AI is doing between the candidate finishing and the
  // AI's voice actually starting (model call + speech synthesis latency).
  // Without this the screen sits silent and still, and candidates assume the
  // interview has frozen. `aiThinking` holds the contextual status line while
  // the AI is working; it clears the moment speech starts (or shortly after a
  // response with nothing to say).
  const [aiThinking, setAiThinking] = useState<string | null>(null)
  const [aiHeardSpeech, setAiHeardSpeech] = useState(false)
  const aiThinkingFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const aiResponseHandedOffRef = useRef(false)
  const beginAiThinking = (status: string) => {
    if (aiThinkingFallbackRef.current) clearTimeout(aiThinkingFallbackRef.current)
    aiResponseHandedOffRef.current = false
    setAiHeardSpeech(false)
    setAiThinking(status)
  }
  const clearAiThinking = () => {
    if (aiThinkingFallbackRef.current) clearTimeout(aiThinkingFallbackRef.current)
    aiThinkingFallbackRef.current = null
    setAiThinking(null)
  }
  // Speech started -> "thinking" is over.
  useEffect(() => {
    if (aiVoice.speaking) clearAiThinking()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiVoice.speaking])
  useEffect(() => () => { if (aiThinkingFallbackRef.current) clearTimeout(aiThinkingFallbackRef.current) }, [])
  // What the AI most recently said, shown as a caption near its tile — there
  // is no visible avatar yet (see useAiInterviewerVoice's own docstring for
  // why), so this caption plus the speaking indicator is the whole "AI
  // interviewer" presence on screen for now.
  const [aiUtteranceText, setAiUtteranceText] = useState<string | null>(null)
  // Every AI utterance spoken this ENTIRE session, kept purely so the
  // transcript-echo filter (see useLiveTranscription's `lastAiUtterance`)
  // can check a leaked mic fragment against everything the AI has ever said,
  // not just its latest line. Proven necessary by testing against a real
  // transcript: a leaked fragment can echo the AI's very first greeting many
  // turns later - Azure's STT finalizes a result an unpredictable moment
  // after the audio that produced it, so there's no reliable upper bound on
  // how stale a leaked fragment can be. A plain ref (not state) - mutated
  // directly in handleAiTurnResult and read fresh on the next render that
  // setAiUtteranceText itself triggers, with no need to re-render on its own.
  const aiAllUtterancesRef = useRef<string[]>([])
  // True once a question has actually been revealed - drives the fallback
  // "Start Interview" button (see handleForceStartInterview): shown only
  // before this flips true, as an escape hatch for when the conversational
  // "are you ready" flow gets stuck and never triggers on its own.
  const [aiInterviewStarted, setAiInterviewStarted] = useState(false)
  // The AI's full line, revealed progressively (a "typewriter" reveal, timed
  // to roughly track the TTS playback) rather than popping in all at once -
  // see the streaming effect below. aiUtteranceText (above) always holds the
  // COMPLETE line as soon as it's known (used for length/grace-window math
  // and as the immediate fallback if speech synthesis is unsupported);
  // aiDisplayedText is what's actually shown in the caption.
  const [aiDisplayedText, setAiDisplayedText] = useState<string | null>(null)
  // Blocks the mic control specifically until the AI's very first greeting
  // sentence has finished playing - the mic starts muted by default (see the
  // mount effect below) but a candidate could otherwise still tap unmute
  // WHILE the greeting is still being spoken, reopening the exact leak
  // window that default-muting exists to close. Unlocked once that one
  // opening line finishes (success or failure - a candidate is never left
  // permanently unable to unmute because the greeting itself errored out).
  const [micLockedForGreeting, setMicLockedForGreeting] = useState(true)
  const aiStartedRef = useRef(false)
  // Guards against a candidate's recognized speech firing a SECOND ai-turn
  // call while one is still in flight — simple, sequential turn-taking
  // rather than true interruption/barge-in (see the explicit "Stop talking"
  // interrupt control instead, which the candidate triggers on purpose).
  const aiTurnInFlightRef = useRef(false)
  // The microphone is gated SILENT (see `suppressed` passed to
  // useLiveTranscription, and useLiveTranscription's own doc comment on why
  // gating happens at the audio SOURCE) for the AI's entire ACTUAL speaking
  // duration, plus a trailing grace window for Azure's STT to finish
  // processing/finalizing the tail of it. Two layers, deliberately
  // overlapping, because a single one has repeatedly proven insufficient:
  //
  //  1. Closed the INSTANT the AI decides to speak (beginMicSuppression,
  //     called synchronously as the very first thing in handleAiTurnResult,
  //     before aiUtteranceText is even set or aiVoice.speak() is invoked) -
  //     shuts the gate before a single sample of TTS audio can exist, with
  //     zero dependency on any browser event or React render having
  //     happened yet.
  //  2. Held closed for as long as aiVoice.speaking reports true. That flag
  //     is itself POLLED from window.speechSynthesis.speaking directly (see
  //     useAiInterviewerVoice) rather than derived from a fixed estimate of
  //     how long the text SHOULD take to read - an earlier version guessed
  //     duration from character count, which a real transcript proved
  //     undershoots actual playback often enough to still leak the AI's own
  //     words into the transcript as "the candidate said this" (and from
  //     there straight back into the AI's own conversation history,
  //     corrupting it - also why "I'm ready, let's begin" kept failing:
  //     aiTurnInFlightRef stayed pinned true processing the AI's own echoed
  //     speech, silently dropping the candidate's real replies). Polling the
  //     engine's actual state has no such guessing problem.
  //
  // Once aiVoice.speaking genuinely goes false, a trailing grace window
  // (still proportional to the utterance's length, for STT finalization
  // lag) delays reopening the gate.
  const [aiMicSuppressed, setAiMicSuppressed] = useState(false)
  const aiGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Read inside the effect below via ref (not as a dependency) so a mere
  // caption-text update can't itself retrigger the effect and race against
  // aiVoice.speaking catching up - only an actual speaking-state transition
  // should ever (re)schedule the grace timer.
  const aiUtteranceTextRef = useRef<string | null>(null)
  aiUtteranceTextRef.current = aiUtteranceText
  const beginMicSuppression = () => {
    if (aiGraceTimerRef.current) {
      clearTimeout(aiGraceTimerRef.current)
      aiGraceTimerRef.current = null
    }
    setAiMicSuppressed(true)
  }
  const clearMicSuppression = () => {
    if (aiGraceTimerRef.current) {
      clearTimeout(aiGraceTimerRef.current)
      aiGraceTimerRef.current = null
    }
    setAiMicSuppressed(false)
  }
  useEffect(() => {
    if (!aiMode) return
    if (aiVoice.speaking) {
      if (aiGraceTimerRef.current) {
        clearTimeout(aiGraceTimerRef.current)
        aiGraceTimerRef.current = null
      }
      setAiMicSuppressed(true)
      return
    }
    const graceMs = Math.min(6000, Math.max(1200, (aiUtteranceTextRef.current?.length || 0) * 35))
    aiGraceTimerRef.current = setTimeout(() => {
      setAiMicSuppressed(false)
      aiGraceTimerRef.current = null
    }, graceMs)
    return () => {
      if (aiGraceTimerRef.current) clearTimeout(aiGraceTimerRef.current)
    }
  }, [aiMode, aiVoice.speaking])
  const aiRevealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Shows the AI's whole line in the caption at once; the sentence the AI is
  // actually speaking is highlighted as the voice hook reports progress (see
  // aiSpeechProgress / AiCaptionText). It used to "type out" at a fixed
  // ~100 chars/sec, far ahead of the voice, so the caption finished long
  // before the AI did. (aiRevealTimerRef is kept so a leftover timer from that
  // older behaviour can never keep running.)
  const streamUtteranceText = (fullText: string) => {
    if (aiRevealTimerRef.current) {
      clearInterval(aiRevealTimerRef.current)
      aiRevealTimerRef.current = null
    }
    setAiSpeechProgress(null)
    setAiDisplayedText(fullText)
  }
  useEffect(() => {
    return () => {
      if (aiRevealTimerRef.current) clearInterval(aiRevealTimerRef.current)
    }
  }, [])
  const questionRef = useRef(question)
  questionRef.current = question

  // Flipped true on unmount, checked inside getUserMedia()'s resolution
  // before touching state — avoids the destroy-race camera leak (same
  // pattern as PreJoinPage's unmountedRef/mediaStreamRef).
  const unmountedRef = useRef(false)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const recorderStartedRef = useRef(false)
  const latestRecorderTilesRef = useRef<RecorderTile[]>([])

  useEffect(() => {
    if (!token) {
      setSessionError('This meeting link is invalid or has expired.')
      setLoadingSession(false)
      return
    }
    getSession(token)
      .then((session) => {
        if (unmountedRef.current) return
        setSessionId(session.session_id)
        setChunkIntervalMs(Math.max(1, session.recording_chunk_interval_seconds || 10) * 1000)
        setReconnectTimeoutMs(Math.max(0, session.reconnect_timeout_seconds ?? 60) * 1000)
        setMaxLiveTranscriptSegments(session.max_live_transcript_segments || MAX_LIVE_TRANSCRIPT_SEGMENTS)
        setInterviewMode(session.interview_mode === 'ai' ? 'ai' : 'human')
        setLoadingSession(false)
      })
      .catch(() => {
        if (unmountedRef.current) return
        setSessionError('This meeting link is invalid or has expired.')
        setLoadingSession(false)
      })
  }, [token])

  // TURN/STUN credentials for the peer connection — public/token-gated twin
  // of the recruiter-side authenticated endpoint.
  useEffect(() => {
    if (!token || loadingSession || sessionError) return
    getPublicTurnCredentials(token)
      .then((servers) => { if (!unmountedRef.current) setIceServers(servers) })
      .catch(() => { if (!unmountedRef.current) setIceServers([{ urls: 'stun:stun.l.google.com:19302' }]) })
  }, [token, loadingSession, sessionError])

  // Acquire this page's own local camera/mic once the session is confirmed
  // valid. Deliberately not carried over from PreJoinPage — that component
  // already released its stream on unmount before this page ever mounted.
  useEffect(() => {
    if (loadingSession || sessionError) return
    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaError(true)
      return
    }
    navigator.mediaDevices
      // Acoustic echo cancellation is requested EXPLICITLY rather than left to
      // the browser's implicit default for a bare `audio: true`. That default
      // is not reliably on, and when it is off a participant hears their own
      // voice played back out of their own speakers via the other side's mic.
      // Same three constraints as useMediaRecorder.ts's mic capture.
      .getUserMedia({
        video: true,
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      .then((stream) => {
        if (unmountedRef.current) {
          // Gone by the time permission resolved — release immediately
          // rather than leaving a live camera/mic nothing will ever stop.
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
        mediaStreamRef.current = stream
        setMediaStream(stream)
      })
      .catch(() => {
        if (unmountedRef.current) return
        setMediaError(true)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSession, sessionError])

  /**
   * Ends the in-flight capture and resolves everything it recorded into one
   * Blob, releasing its audio graph either way.
   *
   * It CLAIMS the capture up front (captureRef is cleared before anything
   * awaits), so two callers racing to finish the same recording — the submit
   * handler and the modality effect, say — cannot both stop the same recorder:
   * the loser sees null and resolves harmlessly. Resolves null when there was
   * nothing recording at all.
   *
   * Deliberately does NOT touch `verbalBlobRef`: the finished recording
   * outlives the mixer that produced it, which is what makes a retry possible
   * without re-recording.
   */
  const finalizeVerbalRecording = useCallback((): Promise<Blob | null> => {
    const capture = captureRef.current
    captureRef.current = null
    setVerbalRecording(false)
    if (!capture) return Promise.resolve(null)

    const release = () => {
      capture.mixed.clear()
      if (capture.context.state !== 'closed') capture.context.close().catch(() => { /* already closing */ })
    }
    const toBlob = () =>
      capture.chunks.length
        ? new Blob(capture.chunks, { type: capture.recorder.mimeType || 'audio/webm' })
        : null

    if (capture.recorder.state === 'inactive') {
      release()
      return Promise.resolve(toBlob())
    }
    return new Promise<Blob | null>((resolve) => {
      // onstop fires only after the final ondataavailable, so `chunks` is
      // complete by the time this reads it.
      capture.recorder.onstop = () => resolve(toBlob())
      try {
        capture.recorder.stop()
      } catch {
        resolve(toBlob())
      }
    }).finally(release)
  }, [])

  /** Ends capture and discards its audio graph, keeping any finalized clip. */
  const stopVerbalCapture = useCallback(() => {
    if (!captureRef.current) return
    finalizeVerbalRecording()
  }, [finalizeVerbalRecording])

  const meeting = useLiveInterviewMeeting({
    sessionId,
    localStream: mediaStream,
    iceServers,
    enabled: !loadingSession && !sessionError && !interviewEnded && !!mediaStream && !!iceServers,
    role: 'candidate',
    reconnectTimeoutMs,
    onAppMessage: (message) => {
      if (message?.type === 'show-question' && message.question) {
        setQuestion(message.question)
        setAnswerText('')
        codeRunHistoryRef.current = []
        // cancel, not stop: trailing speech from the previous question would
        // otherwise land in this one's answer (one answerText for every mode).
        dictation.cancel()
        setSubmitted(false)
        setPanelOpen(true)
        // A fresh question gets fresh verbal state — a recording made for the
        // PREVIOUS question must never bleed into this one's answer. This (and
        // a successful submit) is the ONLY place the stored clip is discarded;
        // a failed submit deliberately keeps it so Retry re-sends the same
        // audio. The override comes from the reveal itself (the recruiter is
        // the single source of truth for it), defaulting to off.
        stopVerbalCapture()
        verbalBlobRef.current = null
        setVerbalSubmitFailed(false)
        setVerbalTextBoxEnabled(message.question.verbal_text_box_enabled === true
          || message.verbal_text_box_enabled === true)
      } else if (message?.type === 'hide-question') {
        setQuestion(null)
        stopVerbalCapture()
      } else if (message?.type === 'force-submit' && message.question_id === question?.id && !submitted && !submitting) {
        // The recruiter clicked "Move Forward" — submit whatever is
        // currently entered for this question (partial code, an in-progress
        // whiteboard sketch, typed text, or a recording already captured)
        // through the exact same path the candidate's own "Submit Answer"
        // button uses, against THIS browser's current state — that state
        // never syncs to the recruiter live, so only this browser can act on
        // it. handleSubmitAnswer sends 'answer-submitted' back on success,
        // which is what resolves the recruiter's wait; if there is truly
        // nothing to submit yet (e.g. a verbal question with no recording
        // started), it no-ops here and the recruiter falls back to recording
        // this as unanswered after its own timeout.
        handleSubmitAnswer()
      } else if (message?.type === 'verbal-text-box-state') {
        // The recruiter toggled "Show Text Box" mid-question. The effect below
        // starts/stops capture off the resulting answer modality, so nothing
        // else is needed here.
        setVerbalTextBoxEnabled(message.enabled === true)
      } else if (message?.type === 'peer-left') {
        // The tile is already gone; this is purely the notification that was
        // missing. Suppressed for a reconnect-eviction — see peerLeftNotice.
        const notice = peerLeftNotice(message)
        if (notice) toast(notice, { icon: '👋' })
      } else if (message?.type === 'interview-ended') {
        // The recruiter ended the interview for everyone. Tear down here and
        // now rather than leaving the candidate in a room whose session is
        // already completed until they happen to refresh. In AI mode the
        // AI's closing line is allowed to finish first.
        if (aiMode) waitForAiSpeech().then(() => setInterviewEnded(true))
        else setInterviewEnded(true)
      } else if (message?.type === 'recording-state') {
        setIsRecording(message.is_recording === true)
      } else if (message?.type === 'chat' && message.id) {
        setChatMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message as LiveInterviewChatMessage]))
        // Only the recruiter's messages count as unread here (this is the
        // candidate's room), and only while the Chat tab isn't already on screen.
        if (message.sender_role !== 'candidate' && !(panelOpenRef.current && activeTabRef.current === 'chat')) {
          setUnreadChat((n) => n + 1)
        }
      } else if (message?.type === 'transcript-segment' && message.id) {
        // Same dedupe-by-id guard as chat above — this browser's own emitted
        // segments echo back here too (server broadcasts to the whole room,
        // sender included), so this is the single source of truth for the
        // combined, both-roles transcript.
        setTranscriptSegments((prev) => {
          if (prev.some((s) => s.id === message.id)) return prev
          const next = [...prev, message as LiveTranscriptSegment]
          return next.length > maxLiveTranscriptSegments ? next.slice(-maxLiveTranscriptSegments) : next
        })
      } else if (message?.type === 'typing') {
        // The signaling relay never echoes a generic broadcast back to its
        // own sender, so any 'typing' message received here is always from
        // the recruiter — no self-filtering needed.
        setOtherTyping(!!message.is_typing)
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
        if (message.is_typing) {
          typingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 5000)
        }
      }
    },
  })
  const { sendAppMessage } = meeting

  // Stores an integrity event and alerts the recruiter's room live (an
  // 'integrity-event' app message - see RecruiterRoomPage's onAppMessage).
  reportIntegrityRef.current = (type, details) => {
    if (!token) return
    recordIntegrityEvent(token, type, details).catch(() => {})
    sendAppMessage({ type: 'integrity-event', event_type: type, at: new Date().toISOString() })
  }

  // Human mode, during the call: tab switching, copy/cut/paste (blocked, and
  // logged) and an extra display connected mid-call. (AI mode records the
  // whole screen instead; Proctored has its own fullscreen/strike rules.)
  // Each type is reported at most once per INTEGRITY_BEHAVIOUR_THROTTLE_MS so
  // e.g. repeated paste attempts don't flood the log or the recruiter.
  const integrityBehaviourActive = !aiMode && !loadingSession && !sessionError && !interviewEnded && !!mediaStream
  const lastBehaviourReportRef = useRef<Record<string, number>>({})
  useEffect(() => {
    if (!integrityBehaviourActive) return
    const report = (type: string, details?: Record<string, any>) => {
      const now = Date.now()
      const last = lastBehaviourReportRef.current[type] || 0
      if (now - last < INTEGRITY_BEHAVIOUR_THROTTLE_MS) return
      lastBehaviourReportRef.current[type] = now
      reportIntegrityRef.current(type, details)
    }
    const onVisibilityChange = () => {
      if (document.hidden) report('tab_hidden')
    }
    const onClipboard = (e: ClipboardEvent) => {
      e.preventDefault()
      toast.error('Copy, cut, and paste are disabled during the interview.', { id: 'clipboard-blocked' })
      report(`${e.type}_attempt`)
    }
    // Chromium's window.screen fires 'change' when a display is connected or
    // removed; only a NEW extra display is reported.
    const screenObj = window.screen as Screen & { isExtended?: boolean } & EventTarget
    let wasExtended = screenObj.isExtended === true
    const onScreenChange = () => {
      const extended = screenObj.isExtended === true
      if (extended && !wasExtended) report('multi_screen_detected', { during_call: true })
      wasExtended = extended
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    document.addEventListener('copy', onClipboard)
    document.addEventListener('cut', onClipboard)
    document.addEventListener('paste', onClipboard)
    if (typeof screenObj.addEventListener === 'function') screenObj.addEventListener('change', onScreenChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      document.removeEventListener('copy', onClipboard)
      document.removeEventListener('cut', onClipboard)
      document.removeEventListener('paste', onClipboard)
      if (typeof screenObj.removeEventListener === 'function') screenObj.removeEventListener('change', onScreenChange)
    }
  }, [integrityBehaviourActive])

  // Dictation listens through the browser's own speech engine, not the
  // meeting track, so the meeting mic is muted while it runs — otherwise the
  // same words also reach the AI / the call as live speech. The candidate can
  // still unmute by hand meanwhile; the mic is only turned back on afterwards
  // if dictation is what muted it.
  const micMutedByDictationRef = useRef(false)
  useEffect(() => {
    if (dictation.listening) {
      if (meeting.micOn) {
        micMutedByDictationRef.current = true
        meeting.toggleMic()
      }
    } else if (micMutedByDictationRef.current) {
      micMutedByDictationRef.current = false
      if (!meeting.micOn) meeting.toggleMic()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictation.listening])

  // AI-interviewer mode only — speaks the AI's line, then acts on whatever it
  // decided: reveal the next question (same shape a 'show-question' socket
  // message would carry, just applied directly instead of round-tripping
  // through the signaling relay — there's no recruiter peer to relay it FROM
  // in this mode) or end the interview. Awaits the speech itself so the
  // question only appears once the AI has actually finished asking for it.
  const handleAiTurnResult = async (result: AiInterviewerResponse, candidateUtterance?: string) => {
    // Closes the mic gate BEFORE anything else - see beginMicSuppression's
    // own doc comment for why this ordering (before the text/caption state
    // even updates) matters.
    beginMicSuppression()
    aiAllUtterancesRef.current.push(result.utterance)
    setAiUtteranceText(result.utterance)
    streamUtteranceText(result.utterance)
    // Speech plays in the BACKGROUND, not awaited before acting on the
    // result - revealing the question (or submitting/ending) no longer
    // waits for the AI to finish talking. Waiting made sense for a live
    // transition (the question naturally arriving right as the sentence
    // describing it ends), but it actively broke RESUME: on a reload
    // mid-question, the candidate already knows the question and had
    // already been staring at an empty panel (no code editor, no timer)
    // through an entire re-read of it before anything reappeared - easily
    // mistaken for the interview being broken. aiVoice.speak() has its own
    // internal safety nets against a wedged browser TTS engine (see
    // useAiInterviewerVoice) so this is safe to fire-and-forget.
    const speech = aiVoice.speak(result.utterance)
    lastAiSpeechRef.current = speech
    void speech
    // Keep "thinking" up until the voice actually starts (speech synthesis
    // has its own startup latency); the fallback covers a muted/failed TTS.
    aiResponseHandedOffRef.current = true
    if (aiThinkingFallbackRef.current) clearTimeout(aiThinkingFallbackRef.current)
    aiThinkingFallbackRef.current = setTimeout(() => setAiThinking(null), 4000)
    if (result.action === 'force_submit') {
      await handleAiForceSubmitRef.current(candidateUtterance)
    } else if (result.action === 'reveal_question' && result.question) {
      const q = result.question
      setQuestion({
        id: q.id,
        question_text: q.question_text,
        skill_name: q.skill_name ?? undefined,
        question_type_id: q.question_type_id ?? undefined,
        response_mode: q.response_mode ?? null,
        category: q.category ?? null,
        test_cases: q.test_cases,
        duration_minutes: q.duration_minutes ?? null,
      })
      setAnswerText('')
      codeRunHistoryRef.current = []
      // cancel, not stop — see the show-question handler above.
      dictation.cancel()
      setSubmitted(false)
      setPanelOpen(true)
      stopVerbalCapture()
      verbalBlobRef.current = null
      setVerbalSubmitFailed(false)
      setVerbalTextBoxEnabled(false)
      setAiInterviewStarted(true)
      // Bring the candidate to the Interview tab the moment a question is revealed,
      // regardless of which tab they were on when the AI decided to start.
      setActiveTab('interview')
    } else if (result.action === 'end_interview') {
      if (result.submit_current && token) {
        // End = submit the current answer, then complete the WHOLE
        // interview (mirrors the recruiter's "End Interview"). The submit
        // itself normally completes it (advance_after_answer sees the end
        // request); endAiInterviewer is the idempotent guarantee for when
        // there was nothing to submit or the submit failed.
        await handleAiForceSubmitRef.current(candidateUtterance)
        try {
          const ended = await endAiInterviewer(token)
          if (ended.utterance && ended.utterance.trim() !== (result.utterance || '').trim()) {
            // Let the current line finish first — speak() cuts off whatever
            // is playing, which used to swallow the AI's goodbye.
            await waitForAiSpeech()
            lastAiSpeechRef.current = aiVoice.speak(ended.utterance)
          }
        } catch { /* the session summary still reflects whatever was submitted */ }
      }
      // The AI's closing words play to the end before the thank-you screen.
      await waitForAiSpeech()
      setInterviewEnded(true)
    }
  }

  // AI-interviewer mode: every recognized fragment is buffered until the
  // candidate has been silent for AI_TURN_SILENCE_MS (the timer restarts on
  // each new fragment AND on live interim speech), then the whole message
  // goes to the AI as ONE turn. Anything spoken while a turn is in flight
  // stays buffered and is sent right after it returns — never dropped.
  const aiPendingTextRef = useRef<string[]>([])
  const aiFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushAiTurn = () => {
    if (!token || aiTurnInFlightRef.current) return
    const text = aiPendingTextRef.current.join(' ').replace(/\s+/g, ' ').trim()
    aiPendingTextRef.current = []
    if (!text) { setAiHeardSpeech(false); return }
    aiTurnInFlightRef.current = true
    beginAiThinking('Thinking about your response…')
    sendAiInterviewerTurn(token, text)
      .then((result) => {
        // The backend detected this as the candidate's mic picking up the
        // AI's own voice — a true no-op, exactly as if nothing was sent.
        if (result.no_op) { clearAiThinking(); return }
        return handleAiTurnResult(result, text)
      })
      .catch(() => { clearAiThinking() /* a missed turn just means the AI doesn't respond to that one message */ })
      .finally(() => {
        aiTurnInFlightRef.current = false
        if (aiPendingTextRef.current.length) scheduleAiFlush()
      })
  }
  const flushAiTurnRef = useRef(flushAiTurn)
  flushAiTurnRef.current = flushAiTurn
  const scheduleAiFlush = () => {
    if (aiFlushTimerRef.current) clearTimeout(aiFlushTimerRef.current)
    aiFlushTimerRef.current = setTimeout(() => flushAiTurnRef.current(), AI_TURN_SILENCE_MS)
  }
  useEffect(() => () => { if (aiFlushTimerRef.current) clearTimeout(aiFlushTimerRef.current) }, [])

  // Timestamp-Linked Feedback Annotations: live transcription — this
  // browser's OWN mic streamed straight to Azure, same as the recruiter
  // room. `enabled` mirrors `meeting`'s own condition.
  const { transcriptionStatus, interimText } = useLiveTranscription({
    role: 'candidate',
    mediaStream,
    enabled: !loadingSession && !sessionError && !interviewEnded && !!mediaStream,
    sendAppMessage,
    getToken: () => getPublicSpeechToken(token || ''),
    // AI-interviewer mode only. Forwarded to the AI any time EXCEPT while a
    // VERBAL question is active - that's the one case where this same speech
    // stream IS the graded answer itself (see advance_after_answer, which
    // paces on submitted answers, never on speech), so treating it as a chat
    // message to the AI would be ambiguous/wrong. For every other case - no
    // question on screen (pre/post-interview chat), or an active coding/
    // whiteboard/text question - the candidate's speech can only mean they're
    // talking TO the AI (asking something, thinking out loud), so it's safe
    // to forward: this is what lets the candidate interrupt mid-question
    // ("wait, what does this mean?") and get a reply without derailing
    // whatever they're typing/drawing.
    // See aiMicSuppressed's own doc comment - this is the actual fix for the
    // AI's own synthesized voice (picked up by the candidate's mic,
    // regardless of whether their mic button shows on or off) being
    // mistranscribed and mislabeled as the candidate speaking: while the AI
    // is talking, or within a trailing window after, the mic is gated
    // SILENT before Azure's recognizer ever sees it.
    suppressed: aiMode && aiMicSuppressed,
    lastAiUtterance: aiMode ? aiAllUtterancesRef.current.join(' ') : null,
    // Buffered, not sent per fragment: Azure finalizes a "recognized"
    // segment at every short pause, so one answer spoken with breaks used to
    // reach the AI as several separate turns (each getting its own reply,
    // or being dropped while a turn was in flight). See flushAiTurn.
    onRecognized: !aiMode ? undefined : (text: string) => {
      // Answer-box dictation is the answer, never a message to the AI.
      if (dictation.listening) return
      // A verbal question's speech is the graded answer — except speech
      // addressed to the AI itself ("I'm done", "next question", "end the
      // interview", "repeat the question"), which must still reach it so the
      // AI keeps listening during verbal questions too.
      if (questionRef.current && answerModality === 'verbal' && !isAddressedToAi(text)) return
      aiPendingTextRef.current.push(text)
      setAiHeardSpeech(true)
      scheduleAiFlush()
    },
  })

  // Still talking (live interim words arriving) — push the send back so a
  // mid-sentence pause longer than Azure's segment boundary doesn't cut
  // the message in two.
  useEffect(() => {
    if (aiMode && interimText && aiPendingTextRef.current.length) scheduleAiFlush()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiMode, interimText])

  // The AI interviewer has no recruiter to wait for — it greets the candidate
  // itself, the instant their camera/mic are ready. Recording is likewise
  // started right here rather than waiting for a 'recording-state' broadcast
  // that will never arrive in this mode (see isRecording's own doc comment).
  useEffect(() => {
    if (!aiMode || !token || loadingSession || sessionError || !mediaStream || aiStartedRef.current) return
    // The whole screen is recorded in AI mode — wait until it's shared.
    if (screenShareStatus === 'pending') return
    aiStartedRef.current = true
    // AI-mode candidates start MUTED - unlike every other case (human-mode
    // candidate, recruiter room), which starts unmuted. The AI's very first
    // line (the greeting) plays before the candidate has had any chance to
    // react or mute themselves manually, and that opening line was the
    // single most consistently reported source of a leaked-audio echo (see
    // ai_interviewer.py's _is_echo_of_ai and useLiveTranscription's
    // `suppressed`/`lastAiUtterance` for the layered defenses against that
    // same class of bug). This removes the window entirely for the one
    // moment none of those could fully close - the candidate unmutes
    // themselves once they're actually ready to speak.
    if (meeting.micOn) meeting.toggleMic()
    setIsRecording(true)
    beginAiThinking('Getting your interviewer ready…')
    startAiInterviewer(token)
      .then(handleAiTurnResult)
      .catch(() => { clearAiThinking(); toast.error('Could not start the AI interviewer. Please refresh and try again.') })
      .finally(() => setMicLockedForGreeting(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiMode, token, loadingSession, sessionError, mediaStream, screenShareStatus])

  // useLiveInterviewMeeting returns a fresh object literal on every render, so
  // `meeting` can never be a useCallback/useEffect dependency — doing so would
  // rebuild startVerbalCapture each render and make the capture effect below
  // stop and restart recording on every single render, losing the answer. The
  // ref gives the callbacks current mic state without any of that churn.
  const meetingRef = useRef(meeting)
  meetingRef.current = meeting

  // Chat history backlog — the live WS `chat` broadcast (above) covers
  // everything sent from here on.
  useEffect(() => {
    if (!token) return
    getPublicChatMessages(token)
      .then((history) => setChatMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id))
        return [...history.filter((m) => !existingIds.has(m.id)), ...prev]
      }))
      .catch(() => { /* chat history is a convenience, not load-blocking */ })
  }, [token])

  // Timestamp-Linked Feedback Annotations: transcript history backlog — same
  // pattern as chat above. Without this, a candidate who reloads mid-
  // interview (or opens the link fresh partway through) would see the live
  // transcript tab start empty, even though everything said before they
  // (re)joined is already persisted.
  useEffect(() => {
    if (!token) return
    getPublicTranscript(token)
      .then((history) => setTranscriptSegments((prev) => {
        const existingIds = new Set(prev.map((s) => s.id))
        const merged = [...history.filter((s) => !existingIds.has(s.id)), ...prev]
        return merged.length > maxLiveTranscriptSegments ? merged.slice(-maxLiveTranscriptSegments) : merged
      }))
      .catch(() => { /* transcript history is a convenience, not load-blocking */ })
  }, [token, maxLiveTranscriptSegments])

  const handleSendChat = (text: string) => {
    sendAppMessage({ type: 'chat', text })
  }

  const handleTypingChange = (isTyping: boolean) => {
    sendAppMessage({ type: 'typing', is_typing: isTyping })
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key as 'interview' | 'chat' | 'transcript')
    if (key === 'chat') {
      setUnreadChat(0)
      if (!panelOpen) setPanelOpen(true)
    }
  }

  const handleMarkChatSeen = useCallback(
    (messageId: string) => (token ? markPublicChatSeen(token, messageId) : Promise.resolve()),
    [token]
  )

  // --- Recording -------------------------------------------------------
  // Tiles come from MeetingStage's off-screen pool, never from the visible
  // grid (see MeetingStage's header).
  const handleRecorderTiles = useCallback((tiles: RecorderTile[]) => {
    latestRecorderTilesRef.current = tiles
    if (recorderStartedRef.current) recorder.updateTiles(tiles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Recruiter-controlled, not automatic — mirrors RecruiterRoomPage's own
  // Start/Stop Recording toggle. This side has no button of its own; it just
  // follows the 'recording-state' broadcast (isRecording) the recruiter's
  // toggle sends, starting/stopping in lockstep with it rather than always
  // running from the moment media comes up.
  useEffect(() => {
    if (!token || !mediaStream) return
    if (isRecording && !recorderStartedRef.current) {
      recorderStartedRef.current = true
      recorder.start(
        (index, chunk) => postRecordingChunk(token, index, chunk),
        latestRecorderTilesRef.current,
        chunkIntervalMs
      )
    } else if (!isRecording && recorderStartedRef.current) {
      recorder.stop()
      recorderStartedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mediaStream, chunkIntervalMs, isRecording])

  // Stop the recorder and release the camera/mic on unmount — even if the
  // candidate navigates away mid-call.
  useEffect(() => {
    return () => {
      unmountedRef.current = true
      recorder.stop()
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Same teardown as unmount, but triggered by the recruiter's End Interview
  // rather than by navigation. Dropping the local stream also flips the
  // meeting hook's `enabled` false, which closes the signaling socket and
  // every peer connection.
  useEffect(() => {
    if (!interviewEnded) return
    recorder.setScreenSource(null)
    screenStreamRef.current?.getTracks().forEach((t) => t.stop())
    screenStreamRef.current = null
    recorder.stop()
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
    mediaStreamRef.current = null
    setMediaStream(null)
    setQuestion(null)
    // Covers the one path that isn't already silent by the time this fires:
    // the AI's own closing line already finished playing before its own
    // `end_interview` action sets this (handleAiTurnResult awaits the speech
    // first) - but a session ended from OUTSIDE this tab entirely (e.g. an
    // admin aborting it directly) can arrive while the AI is still mid-line.
    aiVoice.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewEnded])

  // --- Verbal answer recording: mixing both sides of the conversation ----
  //
  // The clip submitted for a spoken answer is NOT just the candidate's
  // microphone. An interviewer's question, their follow-ups and their
  // interruptions are all part of what makes a spoken answer intelligible, and
  // a transcript of one half of a conversation reads as nonsense — so every
  // participant's audio is mixed into a single stream via a Web Audio graph
  // (each source -> one MediaStreamAudioDestinationNode) and THAT is what gets
  // recorded.
  //
  // This lives in the page rather than in useLiveInterviewCall because
  // everything it needs is already published by the meeting hook
  // (`remoteTiles`, `micOn`, `toggleMic`) and nothing else in the app wants an
  // audio mixer. Pushing it into the hook would couple WebRTC transport to one
  // question type's answer surface.

  /** Wires one stream's audio into the in-flight capture's mixer, at most once. */
  const addStreamToVerbalCapture = useCallback((stream: MediaStream, key?: string) => {
    const capture = captureRef.current
    if (!capture) return
    if (stream.getAudioTracks().length === 0) return
    const dedupeKey = `${key || 'local'}:${stream.id}`
    if (capture.mixed.has(dedupeKey)) return
    capture.mixed.add(dedupeKey)
    try {
      capture.context.createMediaStreamSource(stream).connect(capture.destination)
    } catch {
      // Benign — e.g. a stream whose audio track has not fully initialized.
      // Drop the key so a later pass can retry this stream.
      capture.mixed.delete(dedupeKey)
    }
  }, [])

  const startVerbalCapture = useCallback(() => {
    if (!VERBAL_CAPTURE_SUPPORTED || captureRef.current || !mediaStream) return

    // Recording a muted microphone produces a clip of the interviewer talking
    // to silence, which then scores as an unanswered question. Unmuting is the
    // only recoverable option and it is announced, never silent — the
    // candidate must know their mic is live.
    const currentMeeting = meetingRef.current
    if (!currentMeeting.micOn) {
      currentMeeting.toggleMic()
      toast('Your microphone was turned on so your spoken answer can be recorded.', { icon: '🎤' })
    }

    const AudioContextCtor: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const context = new AudioContextCtor()
    const destination = context.createMediaStreamDestination()

    const mimeType = VERBAL_MIME_PREFERENCES.find((t) => MediaRecorder.isTypeSupported(t)) || ''
    const recorder = mimeType
      ? new MediaRecorder(destination.stream, { mimeType })
      : new MediaRecorder(destination.stream)

    // `chunks` is captured by this recorder's own handler, never read through
    // a ref — so a recorder still flushing after the next question has already
    // started cannot append to the new question's recording.
    const chunks: Blob[] = []
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data)
    }

    // Published BEFORE mixing: addStreamToVerbalCapture reads the in-flight
    // capture through this ref, so it has to be visible before any stream is
    // wired in (here or from the mid-question effect below).
    captureRef.current = { context, destination, recorder, chunks, mixed: new Set<string>() }

    addStreamToVerbalCapture(mediaStream)
    currentMeeting.remoteTiles.forEach((tile) => addStreamToVerbalCapture(tile.stream, tile.peerId))

    setVerbalRecording(true)
    recorder.start()
  }, [mediaStream, addStreamToVerbalCapture])

  const modeMeta = useMemo(() => {
    const mode = question?.response_mode || question?.category || 'text'
    if (mode === 'code_editor' || mode === 'coding') return 'code_editor'
    if (mode === 'whiteboard' || mode === 'design') return 'whiteboard'
    if (mode === 'verbal') return 'verbal'
    return 'text'
  }, [question])

  /**
   * Which answer surface actually renders, and which modality is submitted.
   *
   * Only 'verbal' is conditional. A verbal question means RECORDING the spoken
   * answer UNLESS the recruiter has handed this candidate a text box, or this
   * browser has no MediaRecorder at all — either of which falls back to the
   * plain typed box. That fallback is the whole point: a candidate must never
   * end up on a question with no way to answer it, which is exactly what a
   * naive "verbal means record, full stop" rule would do.
   */
  const answerModality: 'text' | 'code_editor' | 'whiteboard' | 'verbal' = useMemo(() => {
    if (modeMeta !== 'verbal') return modeMeta
    return verbalTextBoxEnabled || !VERBAL_CAPTURE_SUPPORTED ? 'text' : 'verbal'
  }, [modeMeta, verbalTextBoxEnabled])

  // Capture runs exactly while a verbal answer is the live answer surface.
  // Driving it from the resolved modality (rather than from each individual
  // trigger) means the reveal, the recruiter's mid-question toggle, the
  // question being hidden and an unsupported browser all converge on one rule
  // instead of four call sites that have to agree.
  useEffect(() => {
    if (answerModality === 'verbal' && !submitted) startVerbalCapture()
    else stopVerbalCapture()
  }, [answerModality, submitted, startVerbalCapture, stopVerbalCapture])

  // A peer whose audio track arrives AFTER recording started — an interviewer
  // joining or reconnecting mid-question — still belongs in the clip. The
  // meeting hook republishes `remoteTiles` on every ontrack, so re-running the
  // mixer over the current tiles here is the React-idiomatic equivalent of
  // VMP's imperative ontrack hook, and addStreamToVerbalCapture's dedupe keeps
  // already-mixed streams from being connected a second time.
  useEffect(() => {
    if (!verbalRecording) return
    meeting.remoteTiles.forEach((tile) => addStreamToVerbalCapture(tile.stream, tile.peerId))
  }, [verbalRecording, meeting.remoteTiles, addStreamToVerbalCapture])

  // Release the microphone if this room is torn down (unmount, or the
  // recruiter ending the interview) while capture is still running.
  useEffect(() => {
    return () => { stopVerbalCapture() }
  }, [stopVerbalCapture])

  const handleSubmitAnswer = async () => {
    if (!token || !question) return

    // Close out the recording — but only if this is not already a RETRY. On a
    // retry the clip is already finalized and its audio graph released, and
    // re-running this would resolve null over the top of a perfectly good
    // recording. That asymmetry IS the retry feature.
    // (finalizeVerbalRecording releases the graph itself, so there is nothing
    // to tear down separately here.)
    if (answerModality === 'verbal' && !verbalBlobRef.current) {
      verbalBlobRef.current = await finalizeVerbalRecording()
    }
    const recordedAnswer = verbalBlobRef.current
    // Nothing captured. Submitting silence would be scored as an unanswered
    // question and drag the ability estimate down, so this blocks rather than
    // silently submitting nothing.
    if (answerModality === 'verbal' && (!recordedAnswer || recordedAnswer.size === 0)) {
      toast.error('No spoken answer was recorded — please answer out loud, or ask the interviewer for a text box.')
      return
    }

    setSubmitting(true)
    if (aiMode) beginAiThinking('Reviewing your answer and preparing what comes next…')
    try {
      let result
      if (answerModality === 'code_editor') {
        result = await submitPublicAnswer(token, question.id, {
          modality: 'code',
          code_content: answerText,
          code_language: codeLanguage,
          run_history: codeRunHistoryRef.current,
        })
      } else if (answerModality === 'whiteboard') {
        const snapshot = whiteboardRef.current && !whiteboardRef.current.isEmpty() ? whiteboardRef.current.getSnapshot() : null
        result = await submitPublicAnswer(token, question.id, { modality: 'whiteboard', whiteboard_image_data: snapshot, text_content: answerText })
      } else if (answerModality === 'verbal') {
        // 'verbal' is a UI-only distinction — it decides which answer surface
        // renders. On the wire it has always been modality "audio"; what it
        // now carries is the REAL recorded audio rather than a transcript this
        // browser guessed at. The server transcribes it with Whisper and
        // scores the resulting text (see live_interview_public.submit_public_
        // answer), so no `transcript` is sent from here at all.
        result = await submitPublicAnswer(token, question.id, {
          modality: 'audio',
          audio_base64: await blobToBase64(recordedAnswer as Blob),
          audio_format: containerExtension((recordedAnswer as Blob).type),
        })
      } else {
        result = await submitPublicAnswer(token, question.id, { modality: 'text', text_content: answerText })
      }
      setSubmitted(true)
      // The answer is in and scored — this is one of the only two places the
      // recording is discarded (the other being a genuinely new question).
      stopVerbalCapture()
      verbalBlobRef.current = null
      setVerbalSubmitFailed(false)
      // The submission above went to the PUBLIC, token-gated endpoint, which
      // the recruiter's room has no part in and no way to observe — so
      // without this signal their panel would keep showing this question as
      // pending until they manually reloaded. It deliberately carries only
      // the question id: the recruiter's room re-fetches its own question
      // list and scoring summary from the REST endpoints it already uses,
      // rather than this trying to ship an answer or a score over the wire.
      sendAppMessage({ type: 'answer-submitted', question_id: question.id })
      // Clear the reveal immediately — the answer is in, and leaving the
      // editor on screen invites a second, silently-ignored submission.
      setQuestion(null)
      toast.success('Answer submitted')
      // AI-interviewer mode: the AI's reaction to this answer landing —
      // a brief transition line plus either the next question or the
      // interview's close — rides along on this same response (see
      // advance_after_answer, which is what actually generated it server-side).
      if (aiMode && result?.ai_utterance) {
        await handleAiTurnResult({ utterance: result.ai_utterance, action: result.ai_action ?? undefined, question: result.ai_question ?? undefined })
      }
    } catch {
      // The recording is deliberately NOT cleared and capture is NOT
      // restarted: the candidate already said their answer, and a network blip
      // or a Whisper timeout must not cost them that. The button becomes
      // "Retry Submission" and the next click re-sends this exact clip.
      if (answerModality === 'verbal') {
        setVerbalSubmitFailed(true)
        toast.error('Could not submit your recorded answer. You can retry — your recording is still here.')
      } else {
        toast.error('Could not submit your answer. Please try again.')
      }
    } finally {
      setSubmitting(false)
      if (aiMode && !aiResponseHandedOffRef.current) clearAiThinking()
    }
  }

  // AI-interviewer mode only — the candidate indicated (don't know it, want
  // to skip it, or say they've finished and want to move on) they're done
  // with the current question (see ai_interviewer._ad_hoc_turn's
  // "move_forward" detection). Force-submits whatever answer content currently
  // exists (possibly nothing at all) through the SAME public submit
  // endpoint handleSubmitAnswer uses, which is what actually advances the
  // interview via advance_after_answer - mirroring the recruiter's own
  // manual "Move Forward" in human mode, just triggered by the AI's
  // understanding of the candidate's intent instead of a click.
  //
  // Deliberately NOT a call to handleSubmitAnswer() itself: that function
  // refuses to submit a verbal question with no recording at all (a
  // deliberate UI guard for a human clicking "Submit Answer" with nothing
  // said) - exactly the situation a "give up" force-submit needs to push
  // through instead of blocking on. `candidateUtterance` (their actual "I
  // don't know" or similar) is sent as the verbal transcript when there's no
  // recording, so scoring reflects what they really said instead of a bare
  // placeholder.
  const handleAiForceSubmit = async (candidateUtterance?: string) => {
    if (!token || !question || submitting || submitted) return
    const q = question
    setSubmitting(true)
    if (aiMode) beginAiThinking('Reviewing your answer and preparing what comes next…')
    try {
      let result
      if (answerModality === 'code_editor') {
        result = await submitPublicAnswer(token, q.id, {
          modality: 'code',
          code_content: answerText,
          code_language: codeLanguage,
          run_history: codeRunHistoryRef.current,
        })
      } else if (answerModality === 'whiteboard') {
        const snapshot = whiteboardRef.current && !whiteboardRef.current.isEmpty() ? whiteboardRef.current.getSnapshot() : null
        result = await submitPublicAnswer(token, q.id, { modality: 'whiteboard', whiteboard_image_data: snapshot, text_content: answerText })
      } else if (answerModality === 'verbal') {
        // The recording is normally still running here (time-up / "move
        // forward" never went through Submit), so close it out first —
        // otherwise everything the candidate said is dropped.
        if (!verbalBlobRef.current) {
          verbalBlobRef.current = await finalizeVerbalRecording()
        }
        if (verbalBlobRef.current && verbalBlobRef.current.size > 0) {
          result = await submitPublicAnswer(token, q.id, {
            modality: 'audio',
            audio_base64: await blobToBase64(verbalBlobRef.current),
            audio_format: containerExtension(verbalBlobRef.current.type),
          })
        } else {
          result = await submitPublicAnswer(token, q.id, {
            modality: 'audio',
            transcript: answerText.trim() || candidateUtterance || null,
          })
        }
      } else {
        result = await submitPublicAnswer(token, q.id, { modality: 'text', text_content: answerText })
      }
      setSubmitted(true)
      stopVerbalCapture()
      verbalBlobRef.current = null
      setVerbalSubmitFailed(false)
      sendAppMessage({ type: 'answer-submitted', question_id: q.id })
      setQuestion(null)
      if (aiMode && result?.ai_utterance) {
        await handleAiTurnResult({ utterance: result.ai_utterance, action: result.ai_action ?? undefined, question: result.ai_question ?? undefined })
      }
    } catch {
      toast.error('Could not move forward. Please try again.')
    } finally {
      setSubmitting(false)
      if (aiMode && !aiResponseHandedOffRef.current) clearAiThinking()
    }
  }

  // AI-interviewer mode only — per-question countdown, driven by the
  // question's own duration_minutes (already generated/persisted for every
  // question; simply never surfaced to the candidate before). Restarts
  // fresh on every new question.id, pauses once submitted (no point ticking
  // down toward a time-up auto-submit for a question that's already been
  // answered), and — when it genuinely reaches zero — force-submits
  // whatever the candidate currently has, through the exact same path a
  // spoken "I'm done, move forward" already uses, so time-up advances the
  // interview exactly like any other "move past this question" trigger.
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState<number | null>(null)
  const timeUpHandledRef = useRef(false)
  // Always the latest render's handleAiForceSubmit, so time-up submits what
  // is in the text box / code editor / whiteboard RIGHT NOW — not the empty
  // answer that existed when this question's countdown started.
  const handleAiForceSubmitRef = useRef(handleAiForceSubmit)
  handleAiForceSubmitRef.current = handleAiForceSubmit
  useEffect(() => {
    timeUpHandledRef.current = false
    if (!aiMode || !question || !question.duration_minutes || submitted) {
      setTimeRemainingSeconds(null)
      return
    }
    const totalSeconds = Math.round(question.duration_minutes * 60)
    const deadline = Date.now() + totalSeconds * 1000
    setTimeRemainingSeconds(totalSeconds)
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setTimeRemainingSeconds(remaining)
      if (remaining <= 0 && !timeUpHandledRef.current) {
        timeUpHandledRef.current = true
        clearInterval(interval)
        handleAiForceSubmitRef.current().catch(() => {})
      }
    }, 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiMode, question?.id, question?.duration_minutes, submitted])

  // Fallback for the candidate-side "Start Interview" button - an escape
  // hatch for when the conversational "are you ready" flow gets stuck (the
  // AI keeps chatting instead of recognizing the candidate wants to begin).
  // Bypasses the AI's own judgment entirely via a deterministic backend call.
  const [forceStarting, setForceStarting] = useState(false)
  // listening: the candidate has spoken and the AI is waiting for them to
  // pause; thinking: a request is in flight / speech is starting up.
  const aiPresence: AiPresenceState = aiVoice.speaking ? 'speaking'
    : aiThinking ? 'thinking'
    : (aiHeardSpeech || (!!interimText && !questionRef.current)) ? 'listening'
    : 'idle'
  const aiPresenceText = aiPresence === 'thinking' ? aiThinking
    : aiPresence === 'listening' ? 'Go ahead — I\'m listening.'
    : null

  const handleForceStartInterview = async () => {
    if (!token || forceStarting || aiInterviewStarted) return
    setForceStarting(true)
    beginAiThinking('Preparing your first question…')
    try {
      const result = await forceStartAiInterviewer(token)
      await handleAiTurnResult(result)
    } catch {
      clearAiThinking()
      toast.error('Could not start the interview. Please try again.')
    } finally {
      setForceStarting(false)
    }
  }

  // Recruiter-side controls (start / move forward / end — see the
  // recruiter room's AiInterviewerControlPanel) are applied server-side and
  // queued; this poll hands each one to handleAiTurnResult exactly like a
  // normal turn result. Skipped while a candidate turn is in flight so the
  // two never interleave. The ref keeps the interval on the latest
  // handleAiTurnResult (it closes over fresh question/answer state).
  const handleAiTurnResultRef = useRef(handleAiTurnResult)
  handleAiTurnResultRef.current = handleAiTurnResult
  useEffect(() => {
    if (!aiMode || !token || interviewEnded) return
    let polling = false
    const interval = setInterval(async () => {
      if (polling || aiTurnInFlightRef.current) return
      polling = true
      try {
        const events = await pollAiInterviewerEvents(token)
        for (const event of events) await handleAiTurnResultRef.current(event)
      } catch {
        /* transient - next tick retries */
      } finally {
        polling = false
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [aiMode, token, interviewEnded])

  // The interviewer's REAL name whenever the signaling server could resolve
  // one (a direct users.id lookup on their connection); a role word is only
  // a fallback for a participant with genuinely no name to show.
  const remoteName = useCallback(
    (peerId: string, index: number) => {
      const state = meeting.peerStates[peerId]
      if (state?.displayName) return state.displayName
      if (state?.role === 'recruiter') return 'Interviewer'
      if (state?.role === 'candidate') return 'Candidate'
      return `Participant ${index + 1}`
    },
    [meeting.peerStates]
  )

  // Leaving is deliberately a plain navigation away, and it is the ONLY
  // hang-up action a candidate ever gets: this page passes no onEndInterview
  // to MeetingStage, so "End Interview" is not rendered here at all. A
  // candidate must never be able to complete/finalize the session (that is
  // the recruiter's action, and the relay drops an `end-interview` message
  // from a non-recruiter socket even if one were forged). Unmount stops the
  // recorder and releases the camera; the meeting continues for everyone else.
  const handleLeave = () => {
    if (!window.confirm('Leave this interview? The interview will continue, and you can rejoin with the same link.')) return
    window.location.href = '/'
  }

  // Teams-style forced leave — candidate-side twin of RecruiterRoomPage's
  // own handling. Once the reconnect budget is genuinely exhausted (not a
  // deliberate eviction — see LiveInterviewConnectionState's own docstring),
  // this candidate is sent back rather than left on a room that looks alive
  // but will never recover on its own. The interview itself is untouched —
  // the candidate can rejoin with the same link.
  useEffect(() => {
    if (meeting.connectionState !== 'failed') return
    toast.error('Connection lost. Leaving the interview…')
    const timer = setTimeout(() => { window.location.href = '/' }, 4000)
    return () => clearTimeout(timer)
  }, [meeting.connectionState])

  // Kicks off the guided tour the first time the candidate is actually
  // connected — tourStartedRef stops a reconnect from ever showing it again.
  // AI mode waits until the entire screen has been shared: until then the
  // "Share entire screen & start" card is up, and the tour would open on top
  // of it. ('unsupported' = this browser can't share, so there's no card.)
  useEffect(() => {
    if (!meeting.connected || tourStartedRef.current) return
    if (aiMode && screenShareStatus === 'pending') return
    tourStartedRef.current = true
    setTourStep(0)
  }, [meeting.connected, aiMode, screenShareStatus])

  if (interviewEnded) {
    // Same two-column "Thank You + optional experience feedback" design as
    // Proctored Interview's own thank-you screen (PublicInterviewPage.tsx) —
    // wider than the plain RoomShell used for loading/error states below, so
    // this gets its own shell rather than squeezing a feedback form into it.
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl shadow-black/30 border border-gray-100 p-8 max-w-3xl w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex flex-col items-center md:items-start text-center md:text-left justify-center">
              <div className="relative w-28 h-28 mb-7 flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-50 to-emerald-100" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <CheckCircleIcon className="w-14 h-14 text-green-500" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">Thank You!</h1>
              <p className="text-base text-gray-500 max-w-sm leading-relaxed">
                This interview has ended. The hiring team will review your responses and follow up with next steps.
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-gray-100 md:mt-0 md:pt-0 md:border-t-0 md:border-l md:pl-8 md:border-gray-100">
              {token && <FeedbackForm token={token} />}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loadingSession) {
    return (
      <RoomShell>
        <LoadingSpinner size="lg" />
      </RoomShell>
    )
  }

  if (sessionError) {
    return (
      <RoomShell>
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <ShieldCheckIcon className="w-7 h-7 text-red-400" />
        </div>
        <p className="text-gray-200">{sessionError}</p>
      </RoomShell>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900 overflow-hidden">
      {/* Visually hidden — feeds useIntegrityMonitor above frames of the
          candidate's own camera; see integrityVideoRef's own doc comment.
          Never shown as a preview; the visible tile is MeetingStage's own. */}
      <video
        ref={integrityVideoRef}
        autoPlay
        playsInline
        muted
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 -z-10 opacity-0"
        style={{ width: 1, height: 1 }}
      />
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-white/10">
        <p className="text-sm text-gray-200 font-medium">Live Interview</p>
        {/* A dropped socket is no longer indistinguishable from a slow first
            connect: 'reconnecting'/'disconnected' say which is happening (the
            control bar's wifi glyph carries the same state visually). */}
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          {aiMode ? 'AI Interviewer'
            : meeting.connectionState === 'reconnecting' ? 'Reconnecting…'
              : meeting.connectionState === 'failed' ? 'Connection lost — leaving…'
                : meeting.connectionState === 'disconnected' ? 'Disconnected — please rejoin with your link'
                  : meeting.connected ? 'Connected' : 'Connecting…'}
          {(aiMode || meeting.connected) && isRecording && (
            <span className="inline-flex items-center gap-1 text-red-400">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Recording
            </span>
          )}
        </p>
      </div>

      {/* One-time explanation for why the mic control starts red — without
          this, "why can't the AI hear me" is the obvious first question a
          candidate would have. Disappears the moment they unmute, and never
          reappears (checking the CURRENT mic state, not a dismiss flag,
          means muting again later mid-interview doesn't retrigger it). */}
      {aiMode && !meeting.micOn && (
        <div className="flex-shrink-0 px-4 py-2 bg-amber-950/40 border-b border-white/10">
          <p className="text-xs text-amber-200/90">
            {micLockedForGreeting
              ? "Your microphone starts muted while the AI introduces itself — you'll be able to unmute right after."
              : "Your microphone starts muted. Click the mic button below to unmute when you're ready to talk."}
          </p>
        </div>
      )}

      {aiMode && screenShareStatus === 'pending' && (
        <div className="fixed inset-0 z-50 bg-gray-950/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7 text-center">
            <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4">
              <ShieldCheckIcon className="w-7 h-7 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Share your screen to begin</h2>
            <p className="text-sm text-gray-600 mt-2">
              This AI interview records your entire screen along with your camera and voice. In the next
              dialog, choose <span className="font-semibold">Entire screen</span> and click Share.
            </p>
            <button
              type="button"
              onClick={requestScreenShare}
              disabled={requestingScreen}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {requestingScreen ? 'Waiting for permission…' : 'Share entire screen & start'}
            </button>
          </div>
        </div>
      )}
      {aiMode && screenSharingStopped && !interviewEnded && (
        <div className="flex-shrink-0 px-4 py-2 bg-red-950/50 border-b border-white/10 flex items-center justify-between gap-3">
          <p className="text-xs text-red-100">Screen sharing stopped — your screen is no longer being recorded.</p>
          <button
            type="button"
            onClick={requestScreenShare}
            disabled={requestingScreen}
            className="flex-shrink-0 px-2.5 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-medium disabled:opacity-60"
          >
            Share again
          </button>
        </div>
      )}

      {/* AI is working (model call / speech start-up) — an explicit,
          animated status so a pause never looks like a frozen interview. */}
      {aiMode && aiPresence === 'thinking' && (
        <div className="flex-shrink-0 px-4 py-2.5 border-b border-white/10 flex items-center gap-3 bg-[linear-gradient(90deg,rgba(49,46,129,0.55),rgba(91,33,182,0.45),rgba(49,46,129,0.55))] bg-[length:200%_100%] animate-ai-shimmer">
          <span className="relative flex h-6 w-6 flex-shrink-0 items-center justify-center">
            <span
              className="absolute inset-0 rounded-full animate-ai-orbit"
              style={{ background: 'conic-gradient(from 0deg, transparent 0deg, rgba(196,181,253,0.95) 110deg, transparent 220deg)' }}
            />
            <span className="relative h-4 w-4 rounded-full bg-indigo-950" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-indigo-200 uppercase tracking-wide flex items-center gap-2">
              AI Interviewer is thinking <AiThinkingDots />
            </p>
            <p className="text-xs text-indigo-100/80 truncate">{aiPresenceText}</p>
          </div>
        </div>
      )}
      {aiMode && aiPresence === 'listening' && !aiUtteranceText && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-white/10 bg-emerald-950/40 flex items-center gap-2">
          <AiThinkingDots dotClassName="bg-emerald-300" />
          <p className="text-xs text-emerald-100/90">Listening — the AI will reply once you pause.</p>
        </div>
      )}

      {/* AI interviewer caption — no visible avatar yet (see
          useAiInterviewerVoice's docstring: the target is a lip-synced Azure
          avatar, pending Azure enabling that feature), so this caption plus a
          speaking indicator is the AI's whole on-screen presence for now. */}
      {aiMode && aiUtteranceText && aiPresence !== 'thinking' && (
        <div className="flex-shrink-0 px-4 py-2.5 bg-indigo-950/60 border-b border-white/10 flex items-start gap-2">
          {aiVoice.speaking
            ? <AiVoiceBars className="mt-0.5 h-4 flex-shrink-0" />
            : <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0 bg-indigo-700" />}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-indigo-300 uppercase tracking-wide">
              AI Interviewer{aiPresence === 'listening' ? ' · listening' : ''}
            </p>
            <AiCaptionText
              text={aiDisplayedText || ''}
              spokenChars={aiSpeechProgress && aiSpeechProgress.text === aiDisplayedText ? aiSpeechProgress.spokenChars : null}
            />
          </div>
          {/* Explicit, on-purpose interrupt: automatically detecting a genuine
              interruption vs. the AI's own voice bleeding back through the
              mic isn't reliable with a plain browser mic, so instead of
              guessing, the candidate gets a direct control - one click stops
              the AI immediately and starts listening right away, with no
              trailing suppression window (they clearly want to talk now). */}
          {aiVoice.speaking && (
            <button
              type="button"
              onClick={() => {
                aiVoice.stop()
                clearMicSuppression()
                if (aiRevealTimerRef.current) {
                  clearInterval(aiRevealTimerRef.current)
                  aiRevealTimerRef.current = null
                }
                setAiDisplayedText(aiUtteranceText)
                // Stopped on purpose - drop the "being spoken" highlight.
                setAiSpeechProgress(null)
              }}
              className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-800/70 hover:bg-indigo-800 text-indigo-100 text-xs font-medium"
              title="Stop the AI and talk"
            >
              Stop &amp; talk
            </button>
          )}
        </div>
      )}

      {/* Escape hatch: if the conversational "ready to begin" flow never
          triggers on its own (the AI keeps chatting instead of recognizing
          the candidate wants to start), this lets the candidate force it
          directly rather than being stuck repeating themselves. Hidden the
          instant a question is actually revealed. */}
      {aiMode && !aiInterviewStarted && !interviewEnded && (
        <div className="flex-shrink-0 px-4 py-2 bg-indigo-950/40 border-b border-white/10 flex items-center justify-between gap-2">
          <p className="text-xs text-indigo-200/80">Ready to begin? You can start the interview directly.</p>
          <button
            type="button"
            onClick={handleForceStartInterview}
            disabled={forceStarting}
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-xs font-semibold"
          >
            {forceStarting ? 'Starting…' : 'Start Interview'}
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex">
        <div data-tour="video-stage" className="flex-1 min-w-0 flex min-h-0">
        <MeetingStage
          className="flex-1 min-w-0"
          localStream={mediaStream}
          // Candidates have no user row, so the signaling server resolves no
          // name for them and this is "You" in practice; kept symmetric with
          // the recruiter room rather than hardcoded.
          localName={meeting.ownDisplayName || 'You'}
          screenStream={meeting.screenStream}
          remoteTiles={meeting.remoteTiles}
          remoteName={remoteName}
          peerStates={meeting.peerStates}
          presenterId={meeting.presenterId}
          micOn={meeting.micOn}
          cameraOn={meeting.cameraOn}
          sharing={meeting.sharing}
          screenShareSupported={meeting.screenShareSupported}
          onToggleMic={
            aiMode && micLockedForGreeting
              ? () => toast('Please wait for the AI to finish its greeting before unmuting.', { icon: '🎙️' })
              : meeting.toggleMic
          }
          onToggleCamera={meeting.toggleCamera}
          onToggleScreenShare={meeting.toggleScreenShare}
          reactions={meeting.reactions}
          onSendReaction={meeting.sendReaction}
          chatOpen={panelOpen && activeTab === 'chat'}
          onToggleChat={() => {
            if (panelOpen && activeTab === 'chat') {
              setPanelOpen(false)
            } else {
              handleTabChange('chat')
              setPanelOpen(true)
            }
          }}
          chatUnreadCount={unreadChat}
          onLeave={handleLeave}
          leaveLabel="Leave"
          networkQuality={meeting.networkQuality}
          connectionState={meeting.connectionState}
          mediaError={mediaError}
          aiParticipant={aiMode ? { name: 'AI Interviewer', speaking: aiVoice.speaking, state: aiPresence, statusText: aiPresenceText } : null}
          onRecorderTilesChange={handleRecorderTiles}
        />
        </div>

        <MeetingSidePanel
          open={panelOpen}
          onToggle={() => setPanelOpen((o) => !o)}
          resizable
          defaultWidthRatio={0.7}
          title="Interview"
          tabs={[
            { key: 'interview', label: 'Interview', dataTour: 'tab-interview' },
            { key: 'chat', label: 'Chat', badge: unreadChat, dataTour: 'tab-chat' },
            // Transcript tab hidden from the candidate view for now.
            // { key: 'transcript', label: 'Transcript', dataTour: 'tab-transcript' },
          ]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        >
          {/* Card-based panel styling — the question + answer surface is one
              card, chat another, on the panel's light ground. Visual only:
              every binding below (question fields, answerText, submit
              handler, chat props) is unchanged. */}
          {activeTab === 'interview' && (
          <>
          {question ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3.5 space-y-3">
              <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-blue-700">{question.skill_name || 'Question'}</p>
                  {timeRemainingSeconds !== null && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold font-mono tabular-nums ${
                        timeRemainingSeconds <= 60
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {String(Math.floor(timeRemainingSeconds / 60)).padStart(2, '0')}:
                      {String(timeRemainingSeconds % 60).padStart(2, '0')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-900 mt-0.5">{question.question_text}</p>
              </div>
              {timeRemainingSeconds !== null && timeRemainingSeconds > 0 && timeRemainingSeconds <= 60 && (
                <div className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                  Time is about to complete — your answer will be submitted automatically when the timer hits 0.
                </div>
              )}

              {answerModality === 'code_editor' ? (
                <CodeEditor
                  token={token || ''}
                  questionId={question.id}
                  testCases={question.test_cases || []}
                  value={answerText}
                  onChange={setAnswerText}
                  placeholder="// Write your code here…"
                  language={codeLanguage}
                  onLanguageChange={setCodeLanguage}
                  // Run / Run Test Cases, pointed at Live Interview's OWN
                  // token-gated endpoints. The shared editor defaults to
                  // Proctored Interview's routes, which would 404 against a
                  // Live Interview access token — same shapes, different
                  // router, so only the two calls are overridden and none of
                  // the editor's UI changes. Server-side these are stateless:
                  // running code never creates or mutates an answer row, so a
                  // candidate can iterate freely and only "Submit Answer"
                  // below records anything.
                  enableExecution
                  runCodeFn={async (code, language, stdin) => {
                    const result = await runPublicCode(token || '', question.id, code, language, stdin)
                    codeRunHistoryRef.current = [
                      ...codeRunHistoryRef.current,
                      {
                        action: 'run',
                        at: new Date().toISOString(),
                        code,
                        stdout: result.stdout,
                        stderr: result.stderr,
                        exit_code: result.exit_code,
                      },
                    ]
                    return result
                  }}
                  runTestsFn={async (code, language) => {
                    const result = await runPublicTestCases(token || '', question.id, code, language)
                    codeRunHistoryRef.current = [
                      ...codeRunHistoryRef.current,
                      { action: 'run_tests', at: new Date().toISOString(), code, results: result.results },
                    ]
                    return result
                  }}
                />
              ) : answerModality === 'verbal' ? (
                /* Recording, not transcribing. There is deliberately no live
                   transcript preview any more: transcription happens on the
                   server after submission, so showing a running transcript
                   here would mean either lying about what has been recognized
                   or running a second, contradictory recognizer. The candidate
                   gets the one fact that matters instead — the mic is live and
                   this is being recorded. */
                <div className="space-y-2">
                  <div
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-xs font-medium text-gray-700"
                    aria-live="polite"
                  >
                    <MicrophoneIcon className="w-4 h-4 text-blue-600" aria-hidden="true" />
                    <span
                      className={`w-2 h-2 rounded-full ${
                        verbalRecording ? 'bg-red-500 animate-pulse' : 'bg-gray-300'
                      }`}
                      aria-hidden="true"
                    />
                    <span>
                      {verbalSubmitFailed
                        ? 'Recording saved — submission failed'
                        : verbalRecording
                          ? 'Recording — answer out loud'
                          : 'Recording finished'}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-400">
                    {verbalSubmitFailed
                      ? 'Your recording was kept. Retry Submission sends the same answer again — there is no need to repeat it.'
                      : 'Your spoken answer is recorded and transcribed after you submit. Take your time.'}
                  </p>
                </div>
              ) : answerModality === 'whiteboard' ? (
                <div className="space-y-3">
                  <WhiteboardCanvas key={question.id} ref={whiteboardRef} />
                  <textarea
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Optional: add any notes to go with your sketch…"
                    className="w-full min-h-[70px] border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-y"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  {/* A spoken question the candidate is typing instead —
                      either the recruiter handed them a text box, or this
                      browser can't transcribe speech. Saying which avoids the
                      box looking like a mistake on a question they were just
                      asked out loud. */}
                  {modeMeta === 'verbal' && (
                    <p className="text-[11px] text-gray-500">
                      {verbalTextBoxEnabled
                        ? 'The interviewer has opened a text box for this question — type your answer below.'
                        : 'This browser can’t record audio, so please type your answer below.'}
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-gray-400">
                      {dictation.supported ? 'Type your answer, or use the mic to speak it instead.' : 'Type your answer.'}
                    </p>
                    {dictation.supported && (
                      <button
                        type="button"
                        onClick={dictation.listening ? dictation.stop : dictation.start}
                        title={dictation.listening ? 'Click to stop' : 'Click to speak your answer'}
                        className={`flex-shrink-0 inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                          dictation.listening
                            ? 'bg-red-500 text-white shadow-sm shadow-red-200'
                            : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                        }`}
                      >
                        {dictation.listening ? (
                          <>
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                            </span>
                            Click to stop
                          </>
                        ) : (
                          <>
                            <MicrophoneIcon className="w-3.5 h-3.5" />
                            Click to speak
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <textarea
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Type your answer here…"
                    className={`w-full min-h-[160px] border rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-y transition-colors ${
                      dictation.listening ? 'border-red-300 focus:border-red-400' : 'border-gray-300 focus:border-blue-400'
                    }`}
                  />
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleSubmitAnswer}
                  // Unlike the old live-transcript version, a verbal answer is
                  // never disabled on "nothing captured": whether the clip has
                  // any audio is only knowable once recording is stopped, which
                  // is what the handler does first. It rejects an empty
                  // recording there instead of pre-emptively greying out a
                  // button the candidate has every reason to expect to work.
                  disabled={submitting || submitted}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
                >
                  {submitting ? <LoadingSpinner size="sm" /> : <PaperAirplaneIcon className="w-4 h-4" />}
                  {submitted
                    ? 'Submitted'
                    : submitting
                      ? 'Submitting…'
                      : verbalSubmitFailed
                        ? 'Retry Submission'
                        : 'Submit Answer'}
                </button>
              </div>
            </div>
          ) : submitted ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3.5">
              <p className="text-xs text-gray-500">
                Your answer has been submitted. The interviewer will share the next question here when
                they&apos;re ready.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3.5">
              <p className="text-xs text-gray-400">
                You&apos;re in the interview room. The interviewer will share each question here when
                they&apos;re ready.
              </p>
            </div>
          )}
          </>
          )}

          {activeTab === 'chat' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3.5">
            <LiveInterviewChatPanel
              ownRole="candidate"
              messages={chatMessages}
              onSend={handleSendChat}
              markSeen={handleMarkChatSeen}
              otherTyping={otherTyping}
              onTypingChange={handleTypingChange}
            />
          </div>
          )}

          {/* Transcript tab hidden from the candidate view for now.
          {activeTab === 'transcript' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3.5">
            <LiveInterviewTranscriptPanel
              ownRole="candidate"
              segments={transcriptSegments}
              transcriptionStatus={transcriptionStatus}
              interimText={interimText}
            />
          </div>
          )}
          */}
        </MeetingSidePanel>
      </div>

      {tourStep !== null && (
        <LiveInterviewTour
          step={tourStep}
          onSkip={() => setTourStep(null)}
          onNext={() => setTourStep((s) => (s === null ? null : s + 1))}
          onFinish={() => setTourStep(null)}
        />
      )}
    </div>
  )
}

function RoomShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl shadow-black/30 border border-gray-100 p-8 max-w-sm w-full text-center">
        {children}
      </div>
    </div>
  )
}

// Quick-select feedback options. The `key` is what gets stored and must match
// FEEDBACK_TAGS in live_interview_public.py; the label is display-only, so
// rewording it here never invalidates feedback already collected. Identical
// set to PublicInterviewPage.tsx's own FEEDBACK_TAGS — both features ask the
// same experience questions.
const FEEDBACK_TAGS: { key: string; label: string }[] = [
  { key: 'instructions_clear', label: 'Instructions were clear' },
  { key: 'platform_easy', label: 'Platform was easy to use' },
  { key: 'questions_relevant', label: 'Questions were relevant to the role' },
  { key: 'enough_time', label: 'I had enough time to answer' },
  { key: 'technical_issues', label: 'I experienced technical issues' },
  { key: 'setup_difficult', label: 'Camera / microphone setup was difficult' },
]

/** Optional experience feedback on the "interview has ended" screen — same
 * design as Proctored Interview's own FeedbackForm (PublicInterviewPage.tsx),
 * duplicated here rather than shared since it posts to a Live-Interview-
 * specific endpoint via a different (unauthenticated) API client, and the
 * two candidate flows are otherwise deliberately kept independent.
 *
 * Deliberately after the confirmation message, never blocking it: the
 * candidate has finished, and the first thing they need is certainty that
 * the interview is actually over. Skipping is the default — there is no
 * dismissal step, the form is simply left alone. */
function FeedbackForm({ token }: { token: string }) {
  const [rating, setRating] = useState(0)
  const [hovered, setHovered] = useState(0)
  const [tags, setTags] = useState<string[]>([])
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const toggleTag = (key: string) =>
    setTags((prev) => (prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]))

  const submit = async () => {
    if (rating === 0) return
    setSubmitting(true)
    try {
      await submitFeedback(token, { rating, tags, comment })
      setSent(true)
    } catch {
      toast.error('Could not send your feedback. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <div>
        <p className="text-sm text-gray-500">Thank you for the feedback — it helps us improve.</p>
      </div>
    )
  }

  return (
    <div className="text-left">
      <p className="text-sm font-medium text-gray-900 mb-1">How was your experience?</p>
      <p className="text-xs text-gray-400 mb-4">Optional — this is about the platform, not your answers.</p>

      <div className="flex items-center justify-start gap-1 mb-5">
        {[1, 2, 3, 4, 5].map((star) => {
          // Hover preview falls back to the committed rating, so moving the
          // cursor away doesn't make an already-chosen rating look unset.
          const filled = star <= (hovered || rating)
          return (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              aria-label={`${star} star${star === 1 ? '' : 's'}`}
              className="p-0.5 transition-transform hover:scale-110"
            >
              {filled
                ? <StarIconSolid className="w-7 h-7 text-amber-400" />
                : <StarIcon className="w-7 h-7 text-gray-300" />}
            </button>
          )
        })}
      </div>

      <div className="space-y-2 mb-4">
        {FEEDBACK_TAGS.map((tag) => (
          <label key={tag.key} className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              checked={tags.includes(tag.key)}
              onChange={() => toggleTag(tag.key)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-600 group-hover:text-gray-800">{tag.label}</span>
          </label>
        ))}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Anything else you'd like us to know? (optional)"
        rows={3}
        maxLength={2000}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 mb-4"
      />

      <button
        onClick={submit}
        disabled={rating === 0 || submitting}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
      >
        {submitting ? <LoadingSpinner size="sm" /> : null}
        {submitting ? 'Sending…' : 'Send Feedback'}
      </button>
      {rating === 0 && (
        <p className="text-xs text-gray-400 text-center mt-2">Select a star rating to send.</p>
      )}
    </div>
  )
}

// ============================================================================
// The AI's caption line, following the voice: the sentence being spoken is
// highlighted (words already heard brighter), sentences already said read
// normally, ones not yet said are dimmed. `spokenChars` is how much of `text`
// has been heard (useAiInterviewerVoice's onSpeechProgress); null = no
// progress reported (TTS failed / stopped) or the line has finished -> plain.
function AiCaptionText({ text, spokenChars }: { text: string; spokenChars: number | null }) {
  const sentences = useMemo(() => {
    const result: Array<{ text: string; start: number; end: number }> = []
    const pattern = /[^.!?]+[.!?]*["')\]]*\s*/g
    let match: RegExpExecArray | null
    while ((match = pattern.exec(text)) !== null) {
      if (!match[0]) break
      result.push({ text: match[0], start: match.index, end: match.index + match[0].length })
    }
    return result.length ? result : [{ text, start: 0, end: text.length }]
  }, [text])

  if (spokenChars === null || spokenChars >= text.length) {
    return <p className="text-sm text-indigo-100">{text}</p>
  }
  return (
    <p className="text-sm">
      {sentences.map((s, i) => {
        if (spokenChars >= s.end) {
          return <span key={i} className="text-indigo-100 transition-colors">{s.text}</span>
        }
        if (spokenChars >= s.start) {
          const at = spokenChars - s.start
          return (
            <span key={i} className="rounded bg-indigo-400/20 px-0.5 text-indigo-100 [box-decoration-break:clone] transition-colors">
              <span className="font-semibold text-white">{s.text.slice(0, at)}</span>
              {s.text.slice(at)}
            </span>
          )
        }
        return <span key={i} className="text-indigo-100/45 transition-colors">{s.text}</span>
      })}
    </p>
  )
}

// Guided tour — same mechanism as Proctored Interview's own InterviewTour
// (PublicInterviewPage.tsx): shown once, right when the candidate connects.
// Step 0 is a centered intro card (Skip / Take the tour); steps
// 1..LIVE_INTERVIEW_TOUR_STEPS.length spotlight one element each via the
// `data-tour="..."` attributes placed on the real DOM nodes above. Duplicated
// here rather than imported from PublicInterviewPage — that page exports
// nothing, and the two candidate flows are otherwise kept independent (see
// FeedbackForm's own comment above for the same rationale). The spotlight
// itself is a single absolutely-positioned box with a giant box-shadow (a
// common no-library "hole in the overlay" trick) rather than an SVG mask.
// ============================================================================

const LIVE_INTERVIEW_TOUR_STEPS: { selector: string; title: string; description: string }[] = [
  { selector: '[data-tour="video-stage"]', title: 'Your Video', description: 'This is your live video with the interviewer — your camera and microphone stay on for the whole interview.' },
  { selector: '[data-tour="tab-interview"]', title: 'Interview Panel', description: 'Questions from the interviewer appear here as the call goes on — answer directly in this panel.' },
  { selector: '[data-tour="tab-chat"]', title: 'Chat', description: 'Need to send a quick text instead of speaking? Use the Chat tab any time.' },
  // { selector: '[data-tour="tab-transcript"]', title: 'Live Transcript', description: 'A running transcript of the conversation appears here as you talk.' },
]

function LiveInterviewTour({
  step, onSkip, onNext, onFinish,
}: { step: number; onSkip: () => void; onNext: () => void; onFinish: () => void }) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const spotlightIndex = step - 1 // step 0 is the intro card, which has no target

  useEffect(() => {
    const current = LIVE_INTERVIEW_TOUR_STEPS[spotlightIndex]
    if (!current) {
      setRect(null)
      return
    }
    const el = document.querySelector(current.selector)
    if (!el) {
      setRect(null)
      return
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const measure = () => setRect(el.getBoundingClientRect())
    measure()
    // Re-measure a few times while the smooth scroll above is still animating.
    let ticks = 0
    const interval = setInterval(() => {
      measure()
      if (++ticks > 6) clearInterval(interval)
    }, 100)
    window.addEventListener('resize', measure)
    return () => {
      clearInterval(interval)
      window.removeEventListener('resize', measure)
    }
  }, [spotlightIndex])

  if (step === 0) {
    return (
      <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-1.5">Want a quick tour?</h2>
          <p className="text-sm text-gray-500 mb-5">We'll walk you through the interview room in a few seconds.</p>
          <div className="flex items-center gap-2">
            <button onClick={onSkip} className="flex-1 px-4 py-2.5 bg-white text-gray-600 border border-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">
              Skip
            </button>
            <button onClick={onNext} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
              Take the tour
            </button>
          </div>
        </div>
      </div>
    )
  }

  const current = LIVE_INTERVIEW_TOUR_STEPS[spotlightIndex]
  if (!current || !rect) return null
  const isLast = spotlightIndex === LIVE_INTERVIEW_TOUR_STEPS.length - 1

  // Neither "above" nor "below" is guaranteed to have room: "video-stage"
  // spans nearly the full viewport height, leaving no space on either side.
  // `placeAbove`/`placeBelow` each require real clearance (170px, a rough
  // card-height budget) before being chosen; when NEITHER fits, the card
  // anchors near the bottom of the viewport instead of computing a
  // `top`/`bottom` from the target's own (off-screen) edge — which
  // previously placed it below the visible viewport entirely, with no way
  // to scroll to it (exactly what made the tour look stuck on this step).
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top
  const placeAbove = spaceBelow < 170 && spaceAbove >= 170
  const placeBelow = spaceBelow >= 170
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - 300)

  return (
    <>
      {/* Blocks interaction with the page underneath while the tour is active */}
      <div className="fixed inset-0 z-[70]" />
      <div
        className="fixed z-[71] rounded-lg pointer-events-none transition-all duration-200"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          border: '2px solid #2563EB',
        }}
      />
      <div
        className="fixed z-[72] bg-white rounded-xl shadow-xl p-4 w-72 transition-all duration-200"
        style={
          placeAbove ? { bottom: window.innerHeight - rect.top + 12, left }
            : placeBelow ? { top: rect.bottom + 12, left }
              : { bottom: 12, left }
        }
      >
        <p className="text-xs font-medium text-blue-600 mb-1">Step {step} of {LIVE_INTERVIEW_TOUR_STEPS.length}</p>
        <p className="text-sm font-semibold text-gray-900 mb-1">{current.title}</p>
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">{current.description}</p>
        <div className="flex items-center justify-between">
          <button onClick={onSkip} className="text-xs font-medium text-gray-400 hover:text-gray-600">Skip tour</button>
          <button
            onClick={isLast ? onFinish : onNext}
            className="px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
          >
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </>
  )
}
